import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Facebook, Instagram, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import CookieBanner from './CookieBanner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7b2575e8-2794-4e8b-bc83-0e95eaa81cc0/artifacts/emv59acj_Untitled%20design%20-%202026-02-07T124819.889.png";

const servicesItems = [
  { title: 'Professional Readiness Training', href: '/services#professional-readiness', description: 'Build skills to enter or re-enter the workforce' },
  { title: 'Career Development & Guidance', href: '/services#career-development', description: 'Get clarity on your next career move' },
  { title: 'Life Skills & Personal Growth', href: '/services#life-skills', description: 'Strengthen foundations for career success' },
  { title: 'Mentorship & Community Support', href: '/services#mentorship', description: 'Grow with people who understand your journey' },
];

const resourcesItems = [
  { title: 'Library', href: '/resources', description: 'Templates, worksheets, and guides' },
  { title: 'Blogs', href: '/blogs', description: 'Insights and articles' },
  { title: 'FAQ', href: '/faq', description: 'Frequently asked questions' },
  { title: 'Events', href: '/events', description: 'Workshops and community gatherings' },
];

export const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  // Remove Emergent badge on mount
  useEffect(() => {
    const removeEmergentBadge = () => {
      const badges = document.querySelectorAll('[class*="emergent"], [data-emergent], .emergent-badge, #emergent-badge');
      badges.forEach(badge => {
        if (badge) badge.remove();
      });
      // Also check for fixed positioned elements in bottom right
      const fixedElements = document.querySelectorAll('div[style*="position: fixed"]');
      fixedElements.forEach(el => {
        const style = el.getAttribute('style') || '';
        if (style.includes('bottom') && style.includes('right') && !el.classList.contains('cookie-banner')) {
          el.remove();
        }
      });
    };
    
    removeEmergentBadge();
    const interval = setInterval(removeEmergentBadge, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-hr-warm-bg">
      {/* Navigation */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
        }`}
        data-testid="main-header"
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Made Bigger */}
            <Link to="/" className="flex-shrink-0 group" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="HR Nest Logo" 
                className="h-20 w-auto transition-transform duration-300 group-hover:scale-105"
                style={{ maxHeight: '80px', objectFit: 'contain' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <Link to="/" className="px-4 py-2 text-hr-text-secondary hover:text-hr-purple font-medium transition-all duration-300 hover:scale-105" data-testid="nav-home">
                      Home
                    </Link>
                  </NavigationMenuItem>
                  
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-hr-text-secondary hover:text-hr-purple font-medium bg-transparent transition-all duration-300 hover:scale-105" data-testid="nav-services-trigger">
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[400px] gap-3 p-4">
                        {servicesItems.map((item) => (
                          <li key={item.title}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className="block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-all duration-300 hover:bg-hr-purple-light hover:translate-x-1 focus:bg-hr-purple-light"
                                data-testid={`nav-service-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <div className="text-sm font-medium text-hr-text-primary">{item.title}</div>
                                <p className="line-clamp-2 text-sm text-hr-text-muted">{item.description}</p>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-hr-text-secondary hover:text-hr-purple font-medium bg-transparent transition-all duration-300 hover:scale-105" data-testid="nav-resources-trigger">
                      Resources
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[300px] gap-3 p-4">
                        {resourcesItems.map((item) => (
                          <li key={item.title}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className="block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-all duration-300 hover:bg-hr-purple-light hover:translate-x-1 focus:bg-hr-purple-light"
                                data-testid={`nav-resource-${item.title.toLowerCase()}`}
                              >
                                <div className="text-sm font-medium text-hr-text-primary">{item.title}</div>
                                <p className="line-clamp-2 text-sm text-hr-text-muted">{item.description}</p>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link to="/shop" className="px-4 py-2 text-hr-text-secondary hover:text-hr-purple font-medium transition-all duration-300 hover:scale-105" data-testid="nav-shop">
                      Shop
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link to="/about" className="px-4 py-2 text-hr-text-secondary hover:text-hr-purple font-medium transition-all duration-300 hover:scale-105" data-testid="nav-about">
                      About
                    </Link>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>

              <Link to="/contact" data-testid="nav-contact-btn">
                <Button className="ml-4 bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-6 py-5 font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105 btn-3d">
                  Contact Us
                </Button>
              </Link>

              {/* Social Media Links */}
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-stone-200">
                <a 
                  href="https://www.facebook.com/profile.php?id=61587779159890" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-9 h-9 rounded-full bg-hr-purple-light flex items-center justify-center text-hr-purple hover:bg-hr-purple hover:text-white transition-all duration-300 hover:scale-110" 
                  aria-label="Facebook"
                  data-testid="nav-facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
                <a 
                  href="https://www.instagram.com/hrnestllc/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-9 h-9 rounded-full bg-hr-teal-light flex items-center justify-center text-hr-teal hover:bg-hr-teal hover:text-white transition-all duration-300 hover:scale-110" 
                  aria-label="Instagram"
                  data-testid="nav-instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a 
                  href="https://www.linkedin.com/company/hr-nest-llc/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-9 h-9 rounded-full bg-hr-purple-light flex items-center justify-center text-hr-purple hover:bg-hr-purple hover:text-white transition-all duration-300 hover:scale-110" 
                  aria-label="LinkedIn"
                  data-testid="nav-linkedin"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-xl hover:bg-hr-purple-light transition-all duration-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4 border-t border-stone-100 animate-fade-in-up" data-testid="mobile-menu">
              <div className="pt-4 space-y-2">
                <Link to="/" className="block px-4 py-3 text-hr-text-secondary hover:text-hr-purple hover:bg-hr-purple-light rounded-xl transition-all duration-300" data-testid="mobile-nav-home">
                  Home
                </Link>
                <div className="px-4 py-3">
                  <span className="text-hr-text-secondary font-medium">Services</span>
                  <div className="pl-4 mt-2 space-y-1">
                    {servicesItems.map((item) => (
                      <Link key={item.title} to={item.href} className="block py-2 text-sm text-hr-text-muted hover:text-hr-purple transition-colors">
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="px-4 py-3">
                  <span className="text-hr-text-secondary font-medium">Resources</span>
                  <div className="pl-4 mt-2 space-y-1">
                    {resourcesItems.map((item) => (
                      <Link key={item.title} to={item.href} className="block py-2 text-sm text-hr-text-muted hover:text-hr-purple transition-colors">
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
                <Link to="/shop" className="block px-4 py-3 text-hr-text-secondary hover:text-hr-purple hover:bg-hr-purple-light rounded-xl transition-all duration-300">
                  Shop
                </Link>
                <Link to="/about" className="block px-4 py-3 text-hr-text-secondary hover:text-hr-purple hover:bg-hr-purple-light rounded-xl transition-all duration-300">
                  About
                </Link>
                <Link to="/contact" className="block px-4 py-3">
                  <Button className="w-full bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full py-5">
                    Contact Us
                  </Button>
                </Link>

                {/* Mobile Social Media Links */}
                <div className="px-4 py-4 flex items-center justify-center gap-4 border-t border-stone-200 mt-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61587779159890" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 rounded-full bg-hr-purple-light flex items-center justify-center text-hr-purple hover:bg-hr-purple hover:text-white transition-all duration-300" 
                    aria-label="Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a 
                    href="https://www.instagram.com/hrnestllc/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 rounded-full bg-hr-teal-light flex items-center justify-center text-hr-teal hover:bg-hr-teal hover:text-white transition-all duration-300" 
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/hr-nest-llc/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 rounded-full bg-hr-purple-light flex items-center justify-center text-hr-purple hover:bg-hr-purple hover:text-white transition-all duration-300" 
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-hr-text-primary text-white relative overflow-hidden" data-testid="main-footer">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <img src={LOGO_URL} alt="HR Nest Logo" className="h-16 w-auto mb-4 brightness-0 invert" />
              <p className="text-stone-400 text-sm leading-relaxed">
                Where people come first. Reset, rebuild, and rise with confidence.
              </p>
              <div className="flex gap-4 mt-6">
                <a href="https://www.facebook.com/profile.php?id=61587779159890" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-stone-400 hover:text-white hover:bg-hr-purple transition-all duration-300 hover:scale-110" aria-label="Facebook" data-testid="footer-facebook">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/hrnestllc/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-stone-400 hover:text-white hover:bg-hr-teal transition-all duration-300 hover:scale-110" aria-label="Instagram" data-testid="footer-instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.linkedin.com/company/hr-nest-llc/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-stone-400 hover:text-white hover:bg-hr-purple transition-all duration-300 hover:scale-110" aria-label="LinkedIn" data-testid="footer-linkedin">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Services</Link></li>
                <li><Link to="/resources" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Resources</Link></li>
                <li><Link to="/events" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Events</Link></li>
                <li><Link to="/about" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">About Us</Link></li>
                <li><Link to="/contact" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Contact</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Terms & Conditions</Link></li>
                <li><Link to="/refund" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Refund Policy</Link></li>
                <li><Link to="/accessibility" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Accessibility</Link></li>
                <li><Link to="/disclaimer" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Disclaimer</Link></li>
                <li><Link to="/cookie-policy" className="text-stone-400 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block text-sm">Cookie Policy</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-lg mb-4">Contact Us</h4>
              <ul className="space-y-3 text-stone-400 text-sm">
                <li>
                  <a href="mailto:HRnestllc@gmail.com" className="hover:text-white transition-colors">HRnestllc@gmail.com</a>
                </li>
                <li>
                  <a href="tel:317-567-3580" className="hover:text-white transition-colors">317-567-3580</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-stone-700 mt-12 pt-8 text-center text-stone-400 text-sm">
            <p>&copy; {new Date().getFullYear()} HR Nest LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
};

export default Layout;
